import React, { useEffect, useState, useCallback } from 'react';
import { Container } from 'react-bootstrap';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSun, faMoon } from '@fortawesome/free-solid-svg-icons';
import './App.css';
import About from './Components/Portfolio/About';
import Skills from './Components/Portfolio/Skills';
import Projects from './Components/Portfolio/Project';
import Contact from './Components/Portfolio/Contact';
import Home from './Components/Home';
import Login from './Components/Login';
import Testimonials from './Components/Testimonial';
import ProjectDetail from './Components/Portfolio/ProjectDetail';
import Awards from './Components/Portfolio/Awards';
import Services from './Components/Portfolio/Services';
import Blog from './Components/Portfolio/Blog';
import BlogDetail from './Components/Portfolio/BlogDetail';

/* ─── SEO map ────────────────────────────────────────────────── */
var SEO_MAP = {
  '/':            { title: 'Jacob Chidi Eugene | Senior Software Engineer',          description: 'Jacob Chidi Eugene is a full-stack developer and creative technologist building modern, high-performance web experiences.' },
  '/about':       { title: 'About Jacob Chidi Eugene | Developer & Creator',         description: 'Learn more about Jacob Chidi Eugene — his journey, values, and approach to building impactful digital products.' },
  '/skills':      { title: 'Skills & Expertise | Jacob Chidi Eugene',                description: 'Explore the technical stack, tools, and competencies that Jacob Chidi Eugene brings to every project.' },
  '/projects':    { title: 'Projects | Jacob Chidi Eugene',                          description: 'A curated showcase of web applications, tools, and creative projects built by Jacob Chidi Eugene.' },
  '/services':    { title: 'Services | Jacob Chidi Eugene',                          description: 'Web development, UI/UX design, consulting, and more — professional services by Jacob Chidi Eugene.' },
  '/contact':     { title: 'Contact Jacob Chidi Eugene',                             description: 'Reach out to Jacob Chidi Eugene for collaborations, project inquiries, or just to say hello.' },
  '/testimonial': { title: 'Testimonials | Jacob Chidi Eugene',                      description: 'Hear what clients and collaborators say about working with Jacob Chidi Eugene.' },
  '/awards':      { title: 'Awards & Honours | Jacob Chidi Eugene',                  description: 'Recognitions and awards received by Jacob Chidi Eugene for contributions to software engineering and education.' },
  '/blog':        { title: 'Blog | Jacob Chidi Eugene',                              description: 'Thoughts, tutorials, and insights on web development, design, and tech — by Jacob Chidi Eugene.' },
};

var DEFAULT_SEO = {
  title: 'Jacob Chidi Eugene | Senior Software Engineer',
  description: 'Jacob Chidi Eugene — full-stack developer, designer, and digital creator.',
};

var CANONICAL_BASE = 'https://jacobchidieugen.com';

function setMetaTag(selector, attrKey, attrValue) {
  var el = document.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    var match = selector.match(/\[([a-z:]+)="([^"]+)"\]/);
    if (match) { el.setAttribute(match[1], match[2]); }
    document.head.appendChild(el);
  }
  el.setAttribute(attrKey, attrValue);
}

function setLinkTag(rel, href) {
  var el = document.querySelector('link[rel="' + rel + '"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function useSEO(pathname) {
  useEffect(function() {
    var seo         = SEO_MAP[pathname] || DEFAULT_SEO;
    var title       = seo.title;
    var description = seo.description;
    var canonical   = CANONICAL_BASE + pathname;

    document.title = title;
    document.documentElement.lang = 'en';

    setMetaTag('meta[name="description"]',         'content', description);
    setMetaTag('meta[name="author"]',              'content', 'Jacob Chidi Eugene');
    setMetaTag('meta[name="robots"]',              'content', 'index, follow');
    setMetaTag('meta[property="og:type"]',         'content', 'website');
    setMetaTag('meta[property="og:title"]',        'content', title);
    setMetaTag('meta[property="og:description"]',  'content', description);
    setMetaTag('meta[property="og:url"]',          'content', canonical);
    setMetaTag('meta[property="og:site_name"]',    'content', 'Jacob Chidi Eugene');
    setMetaTag('meta[property="og:image"]',        'content', CANONICAL_BASE + '/og-cover.jpg');
    setMetaTag('meta[name="twitter:card"]',        'content', 'summary_large_image');
    setMetaTag('meta[name="twitter:title"]',       'content', title);
    setMetaTag('meta[name="twitter:description"]', 'content', description);
    setMetaTag('meta[name="twitter:image"]',       'content', CANONICAL_BASE + '/og-cover.jpg');
    setLinkTag('canonical', canonical);

    var scriptId = 'jce-ld-json';
    var ldScript  = document.getElementById(scriptId);
    if (!ldScript) {
      ldScript      = document.createElement('script');
      ldScript.id   = scriptId;
      ldScript.type = 'application/ld+json';
      document.head.appendChild(ldScript);
    }
    ldScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type':    'Person',
      name:       'Jacob Chidi Eugene',
      url:        CANONICAL_BASE,
      jobTitle:   'Senior Software Engineer',
      description: description,
    });
  }, [pathname]);
}

function usePageTracking(pathname) {
  useEffect(function() {
    if (typeof window.gtag !== 'function') return;
    window.gtag('config', 'G-V7TJXH5JGL', { page_path: pathname });
  }, [pathname]);
}

var NAV_LINKS = [
  { to: '/',            label: 'Home'         },
  { to: '/about',       label: 'About'        },
  { to: '/skills',      label: 'Skills'       },
  { to: '/services',    label: 'Services'     },
  { to: '/projects',    label: 'Projects'     },
  { to: '/blog',        label: 'Blog'         },
  { to: '/testimonial', label: 'Testimonials' },
  { to: '/awards',      label: 'Awards'       },
];

function useTheme() {
  var stored  = localStorage.getItem('jce-theme');
  var initial = stored ? stored : 'dark';
  var themeState = useState(initial);
  var theme      = themeState[0];
  var setTheme   = themeState[1];

  useEffect(function() {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('jce-theme', theme);
  }, [theme]);

  var toggle = useCallback(function() {
    setTheme(function(t) { return t === 'dark' ? 'light' : 'dark'; });
  }, [setTheme]); // eslint-disable-line react-hooks/exhaustive-deps

  return { theme: theme, toggle: toggle };
}

var App = function() {
  var location = useLocation();

  var themeObj    = useTheme();
  var theme       = themeObj.theme;
  var toggleTheme = themeObj.toggle;

  var scrolledState = useState(false);
  var scrolled      = scrolledState[0];
  var setScrolled   = scrolledState[1];

  var menuState   = useState(false);
  var menuOpen    = menuState[0];
  var setMenuOpen = menuState[1];

  var activeLinkState = useState(location.pathname);
  var activeLink      = activeLinkState[0];
  var setActiveLink   = activeLinkState[1];

  useSEO(location.pathname);
  usePageTracking(location.pathname);

  useEffect(function() {
    setMenuOpen(false);
    setActiveLink(location.pathname);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  var handleScroll = useCallback(function() {
    setScrolled(window.scrollY > 60);
  }, [setScrolled]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(function() {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return function() { window.removeEventListener('scroll', handleScroll); };
  }, [handleScroll]);

  var navClass = 'jce-nav'
    + (scrolled  ? ' jce-nav--scrolled' : '')
    + (menuOpen  ? ' jce-nav--open'     : '');

  return (
    <>
      {/* ── Navbar ───────────────────────────────────────────── */}
      <header className={navClass} role="banner">
        <div className="jce-nav__inner">

          {/* Logo */}
          <Link to="/" className="jce-nav__logo" aria-label="Jacob Chidi Eugene — home">
            <img
              src={process.env.PUBLIC_URL + '/jacob.jpeg'}
              alt="Jacob Chidi Eugene"
              width="40"
              height="50"
            />
            <span className="jce-nav__wordmark">
              <span className="jce-nav__wordmark-name">Jacob Chidi Eugene</span>
              <span className="jce-nav__wordmark-role">Snr Software Engineer</span>
            </span>
          </Link>

          {/* Desktop links */}
          <nav className="jce-nav__links" aria-label="Primary navigation">
            {NAV_LINKS.map(function(item) {
              var cls = 'jce-nav__link' + (activeLink === item.to ? ' jce-nav__link--active' : '');
              return <Link key={item.to} to={item.to} className={cls}>{item.label}</Link>;
            })}
          </nav>

          {/* Hire Me CTA */}
          <Link to="/contact" className="jce-nav__cta" aria-label="Hire Jacob">
            Hire Me
          </Link>

          {/* Theme toggle — desktop */}
          <button
            className="jce-theme-toggle"
            onClick={toggleTheme}
            aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            title={theme === 'dark' ? 'Light mode' : 'Dark mode'}
          >
            <FontAwesomeIcon
              icon={theme === 'dark' ? faSun : faMoon}
              className="jce-theme-toggle__icon jce-theme-toggle__icon--enter"
            />
          </button>

          {/* Hamburger */}
          <button
            className={'jce-nav__burger' + (menuOpen ? ' is-active' : '')}
            onClick={function() { setMenuOpen(function(v) { return !v; }); }}
            aria-expanded={menuOpen}
            aria-controls="mobile-menu"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          >
            <span></span><span></span><span></span>
          </button>
        </div>

        {/* ── Mobile drawer ──────────────────────────────────── */}
        <nav
          id="mobile-menu"
          className={'jce-nav__drawer' + (menuOpen ? ' jce-nav__drawer--open' : '')}
          aria-label="Mobile navigation"
        >
          {/* Scrollable link grid */}
          <div className="jce-nav__drawer-body">
            <div className="jce-nav__drawer-grid">
              {NAV_LINKS.map(function(item, index) {
                var isActive = activeLink === item.to;
                var itemCls  = 'jce-nav__drawer-item'
                  + (isActive ? ' jce-nav__drawer-item--active' : '');
                var idx = String(index + 1).padStart(2, '0');
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={itemCls}
                    onClick={function() { setMenuOpen(false); }}
                  >
                    <span className="jce-nav__drawer-idx">{idx}</span>
                    <span className="jce-nav__drawer-link-inner">{item.label}</span>
                  </Link>
                );
              })}
            </div>
          </div>

          {/* Pinned footer — CTA + theme toggle */}
          <div className="jce-nav__drawer-footer">
            <Link
              to="/contact"
              className="jce-nav__drawer-cta"
              onClick={function() { setMenuOpen(false); }}
            >
              Hire Me
            </Link>
            <button
              className="jce-theme-toggle jce-theme-toggle--drawer"
              onClick={toggleTheme}
              aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              title={theme === 'dark' ? 'Light mode' : 'Dark mode'}
            >
              <FontAwesomeIcon icon={theme === 'dark' ? faSun : faMoon} />
            </button>
          </div>
        </nav>
      </header>

      {/* ── Page content ─────────────────────────────────────── */}
      <main id="main-content" className="jce-main" tabIndex={-1}>
        <Container className="jce-container">
          <Routes>
            <Route path="/"             element={<Home />} />
            <Route path="/login"        element={<Login />} />
            <Route path="/about"        element={<About />} />
            <Route path="/skills"       element={<Skills />} />
            <Route path="/projects"     element={<Projects />} />
            <Route path="/projects/:id" element={<ProjectDetail />} />
            <Route path="/contact"      element={<Contact />} />
            <Route path="/testimonial"  element={<Testimonials />} />
            <Route path="/awards"       element={<Awards />} />
            <Route path="/services"     element={<Services />} />
            <Route path="/blog"         element={<Blog />} />
            <Route path="/blog/:id"     element={<BlogDetail />} />
          </Routes>
        </Container>
      </main>

      {/* ── Footer ───────────────────────────────────────────── */}
      <footer className="jce-footer" role="contentinfo">
        <div className="jce-footer__inner">
          <p className="jce-footer__copy">
            &copy; {new Date().getFullYear()} Jacob Chidi Eugene. Crafted with care.
          </p>
          <nav className="jce-footer__nav" aria-label="Footer navigation">
            {NAV_LINKS.slice(0, 5).map(function(item) {
              return (
                <Link key={item.to} to={item.to} className="jce-footer__link">
                  {item.label}
                </Link>
              );
            })}
          </nav>
        </div>
      </footer>
    </>
  );
};

var AppWrapper = function() {
  return (
    <Router>
      <App />
    </Router>
  );
};

export default AppWrapper;